// ----------------------------------------------------------------------
// MICROSOFT SIGN-IN, between leaving for Microsoft and coming back.
//
// "Continue with Microsoft" is Supabase's `azure` provider on the implicit
// flow (supabaseClient.js says why the app is implicit). The browser leaves
// for Microsoft and comes back to the page it left, carrying either a session
// in the fragment or an error in the query and the fragment. Nothing in that
// return says it came from Microsoft, or from which portal's button — so the
// button leaves a note in sessionStorage first (tab-scoped, gone with the
// tab), and the page that comes back reads it once.
//
// This file is the note and the words; JWTContext is what acts on them (it
// is the only session manager, AGENTS.md). Everything here is pure or
// storage-only, so it can be tested without a browser session.
// ----------------------------------------------------------------------
import { AUTH_LINK } from './supabaseClient';

/** Supabase's name for Microsoft Entra ID (Azure AD) — configured in the project. */
export const MICROSOFT_PROVIDER = 'azure';

const SSO_KEY = 'lingoyou.auth.sso';

/** A note older than this is somebody's abandoned attempt, not this one. */
export const SSO_TTL_MS = 15 * 60 * 1000;

const storage = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/**
 * A path on THIS site to come back to — never an absolute or protocol-relative
 * URL, which would make the note an open redirect.
 */
export function safeReturnPath(path) {
  const text = String(path || '');
  if (!text.startsWith('/') || text.startsWith('//') || text.startsWith('/\\'))
    return null;
  return text;
}

/** The note the button left, if it is fresh: `{ provider, portal, returnTo, at }`. */
export function readSsoAttempt(now = Date.now()) {
  try {
    const raw = storage()?.getItem(SSO_KEY);
    if (!raw) return null;
    const attempt = JSON.parse(raw);
    if (!attempt || typeof attempt !== 'object') return null;
    const at = Number(attempt.at);
    // a clock that moved backwards a little is tolerated; a note from the
    // future is not this one
    if (!Number.isFinite(at) || now - at > SSO_TTL_MS || at - now > 60 * 1000)
      return null;
    return {
      provider: String(attempt.provider || MICROSOFT_PROVIDER),
      portal: String(attempt.portal || 'intranet'),
      returnTo: safeReturnPath(attempt.returnTo),
      at
    };
  } catch {
    return null;
  }
}

export function writeSsoAttempt(
  { provider = MICROSOFT_PROVIDER, portal = 'intranet', returnTo } = {},
  now = Date.now()
) {
  try {
    storage()?.setItem(
      SSO_KEY,
      JSON.stringify({
        provider,
        portal,
        returnTo: safeReturnPath(returnTo),
        at: now
      })
    );
  } catch {
    // storage blocked: the return is then read as an ordinary sign-in, which
    // the guards already handle — only the friendlier messages are lost
  }
}

export function clearSsoAttempt() {
  try {
    storage()?.removeItem(SSO_KEY);
  } catch {
    // nothing stored, nothing to clear
  }
}

/**
 * What a return URL says went wrong — GoTrue writes it to the query AND the
 * fragment (`error`, `error_code`, `error_description`). Null when it says
 * nothing.
 */
export function callbackErrorOf(href) {
  try {
    const url = new URL(href);
    const fragment = new URLSearchParams((url.hash || '').replace(/^#/, ''));
    const read = (key) => url.searchParams.get(key) || fragment.get(key) || '';
    const error = read('error');
    const code = read('error_code');
    const description = read('error_description').replace(/\+/g, ' ');
    return error || code || description ? { error, code, description } : null;
  } catch {
    return null;
  }
}

/**
 * The attempt THIS page load is the return of: the note, and the callback the
 * URL carried — a session (AUTH_LINK, captured before supabase-js blanked the
 * fragment) or an error. Read once, at load.
 */
export function ssoReturnOf({ attempt, link, href }) {
  if (!attempt) return null;
  const error = callbackErrorOf(href);
  const tokens = Boolean(link?.type) && !link?.error;
  if (!tokens && !error) return null;
  return { ...attempt, error };
}

const ATTEMPT_AT_LOAD = readSsoAttempt();

export const SSO_RETURN = ssoReturnOf({
  attempt: ATTEMPT_AT_LOAD,
  link: AUTH_LINK,
  href:
    typeof window === 'undefined' ? 'http://localhost/' : window.location.href
});

// A load that carries no callback ends any attempt still on file: the person
// came back some other way (the Back button, a new visit), and a note left
// lying about would make the NEXT ordinary sign-in look like Microsoft's.
if (ATTEMPT_AT_LOAD && !SSO_RETURN) clearSsoAttempt();

/** Takes the callback out of the address bar, so a refresh does not replay it. */
export function cleanCallbackUrl() {
  try {
    const url = new URL(window.location.href);
    ['error', 'error_code', 'error_description'].forEach((key) =>
      url.searchParams.delete(key)
    );
    const search = url.searchParams.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${search ? `?${search}` : ''}`
    );
  } catch {
    // nothing to clean
  }
}

// ----------------------------------------------------------------------
// THE WORDS. Every way back from Microsoft that is not "you are in" says what
// happened and what to do next, and the email and password stay right there
// as the way in that still works.

const FALLBACK = 'sign in with your email and password';

/**
 * A callback that came back with an error. Cancelling is not a failure — it
 * is the person changing their mind — so it says so in the info tone.
 */
export function describeSsoError({
  error = '',
  code = '',
  description = ''
} = {}) {
  const text = `${description}`.toLowerCase();
  // Entra ID answers a cancelled sign-in with `access_denied` (subcode
  // `cancel`), and a refused consent with AADSTS65004
  if (error === 'access_denied' || /cancel|declined|aadsts65004/.test(text)) {
    return {
      severity: 'info',
      message: `Microsoft sign-in was cancelled. Try again, or ${FALLBACK}.`
    };
  }
  if (code === 'provider_email_needs_verification') {
    return {
      severity: 'warning',
      message: `Microsoft did not confirm the email address on this account, so it cannot be matched to your LingoYou login. Please ${FALLBACK}, and let your administrator know.`
    };
  }
  if (
    code === 'signup_disabled' ||
    /signups? not allowed|user not found/.test(text)
  ) {
    return {
      severity: 'warning',
      message: `This Microsoft account is not linked to a LingoYou account. Please ${FALLBACK}, or ask your administrator to link it.`
    };
  }
  if (code === 'user_banned') {
    return {
      severity: 'error',
      message: 'This account is not enabled. Contact your manager.'
    };
  }
  if (
    code === 'provider_disabled' ||
    /provider is not enabled|unsupported provider/.test(text)
  ) {
    return {
      severity: 'error',
      message: `Microsoft sign-in is not available right now. Please ${FALLBACK}.`
    };
  }
  if (/email/.test(text) && /external provider|provider/.test(text)) {
    return {
      severity: 'warning',
      message: `Microsoft did not share an email address for this account, so it cannot be matched to your LingoYou login. Please ${FALLBACK}.`
    };
  }
  return {
    severity: 'error',
    message: `Microsoft sign-in did not complete. Please try again, or ${FALLBACK}.`
  };
}

/**
 * Microsoft said yes and LingoYou said no — or could not be asked. `result`
 * is JWTContext's `fetchAccount` answer; `home` the portal that owns the
 * account when it exists but belongs elsewhere.
 */
export function describeSsoRefusal({
  result = {},
  email = '',
  home = null
} = {}) {
  const who = email ? `${email}` : 'this Microsoft account';
  if (result.account && home) {
    return {
      severity: 'info',
      message: `Microsoft signed you in as ${who}, but that account belongs to the ${home.label} portal. Sign in there instead.`,
      portalPath: home.path,
      portalLabel: home.label
    };
  }
  if (result.account) {
    return {
      severity: 'error',
      message: 'This account cannot be used on this portal.'
    };
  }
  if (result.reason === 'unreachable') {
    return {
      severity: 'error',
      message:
        'Microsoft signed you in, but the LingoYou server could not be reached. Please try again in a moment.'
    };
  }
  if (
    !result.code ||
    result.code === 'UNMAPPED' ||
    result.code === 'NOT_FOUND'
  ) {
    return {
      severity: 'warning',
      message: `${
        email ? `${email} is` : 'This Microsoft account is'
      } not linked to a LingoYou team account yet. Please ${FALLBACK}, or ask your administrator to link it.`
    };
  }
  // deactivated, removed, not enabled: the server knows why, in plain words
  return {
    severity: 'error',
    message: result.message || 'This account is not active on this portal.'
  };
}

/**
 * THE DECISION, once the account has been asked for: in, or out with the
 * words. Pure, so the one place the portal's rule is applied can be tested.
 *
 * @param result        `fetchAccount`'s `{ account, reason, code, message }`
 * @param expectedTypes the account types the portal takes (`PORTAL_TYPES`)
 * @param homeOf        `(type) => { path, label }` — the portal a type owns
 */
export function settleSsoOutcome({ result, expectedTypes, email, homeOf }) {
  if (result?.account && expectedTypes.includes(result.account.type)) {
    return { signedIn: true, account: result.account, error: null };
  }
  const home = result?.account ? homeOf(result.account.type) || null : null;
  return {
    signedIn: false,
    account: null,
    error: describeSsoRefusal({ result: result || {}, email, home })
  };
}
